<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';
import { addContentLogAPI } from '@/api/logServiceApi';
import {
  MONTHLY_FORTUNE_CARD_TYPE_ID,
  MONTHLY_FORTUNE_PARENT_TYPE_ID,
  MONTHLY_FORTUNE_TOC_CARDS,
} from './tocCards.js';
import { getMonthlyCoverCopy } from './monthlyCoverCopy.js';

const props = defineProps({
  monthlyFortuneTicketData: { type: Object, default: () => null },
  monthlyFortuneLogData: { required: false },
  hasPermission: { required: false, default: false },
});

const route = useRoute();
const analysisStore = useAnalysisStore();
const guestUserId = computed(() => route.params.id);

// =============== 보유 이용권 ===============
const ticketCount = computed(() => props.monthlyFortuneTicketData?.totalCount ?? 0);

// =============== 월별 표지 카피 ===============
const monthlyCoverCopy = computed(() =>
  getMonthlyCoverCopy(analysisStore.monthlyFortuneSelectedMonth),
);

// =============== 구매한 내역 (월 탭) ===============
/** 구매이력이 있는 달 (최신순) */
const purchasedTabs = computed(() =>
  guestUserId.value ? analysisStore.getMonthlyFortunePurchasedMonths(guestUserId.value) : [],
);

const isActiveTab = (item) =>
  item.year === analysisStore.monthlyFortuneSelectedYear &&
  item.month === analysisStore.monthlyFortuneSelectedMonth;

const handleSelectTab = (item) => {
  addContentLogAPI({
    contentType: 'am_monthly_panel_tab_opt3',
    option3: `${item.year}-${String(item.month).padStart(2, '0')}`,
  });
  analysisStore.setMonthlyFortuneSelectedMonth(item.year, item.month);
};

const handleCardClick = (card) => {
  addContentLogAPI({
    contentType: 'am_monthly_panel_card_click_opt3',
    option3: card?.key || '',
  });
  analysisStore.requestMonthlyFortuneOpenDrawer(card.key);
};

// =============== 선택된 달의 구매 카드 목록 ===============
const PAID_CARD_TYPE_IDS = [
  MONTHLY_FORTUNE_CARD_TYPE_ID.capability,
  MONTHLY_FORTUNE_CARD_TYPE_ID.mode,
  MONTHLY_FORTUNE_CARD_TYPE_ID.money,
  MONTHLY_FORTUNE_CARD_TYPE_ID.romanceStyle,
  MONTHLY_FORTUNE_CARD_TYPE_ID.caution,
];

const paidCardByTypeId = MONTHLY_FORTUNE_TOC_CARDS.filter((c) => c.isPaid).reduce((acc, c) => {
  acc[c.typeId] = c;
  return acc;
}, {});

/** 선택된 달에 구매한 카드 목차 (tocCards 정의 순서 유지) */
const purchasedCards = computed(() => {
  if (!guestUserId.value) return [];
  const logs = analysisStore.monthlyFortunePurchaseHistory?.[guestUserId.value] || [];
  const year = analysisStore.monthlyFortuneSelectedYear;
  const month = analysisStore.monthlyFortuneSelectedMonth;
  const typeIdsInMonth = new Set();
  logs.forEach((log) => {
    if (log.year !== year || log.month !== month) return;
    if (log.analysisTypeId === MONTHLY_FORTUNE_PARENT_TYPE_ID) {
      PAID_CARD_TYPE_IDS.forEach((tid) => typeIdsInMonth.add(tid));
    } else if (paidCardByTypeId[log.analysisTypeId]) {
      typeIdsInMonth.add(log.analysisTypeId);
    }
  });
  return PAID_CARD_TYPE_IDS
    .filter((tid) => typeIdsInMonth.has(tid))
    .map((tid) => paidCardByTypeId[tid]);
});

onMounted(() => {
  if (guestUserId.value) {
    analysisStore.fetchMonthlyFortunePurchaseHistory(guestUserId.value);
  }
});
</script>

<template>
  <div class="mf-panel">
    <!-- 보유 월운세분석권 -->
    <div class="mf-panel__ticket">
      <div class="mf-panel__ticket-left">
        <i class="mf-panel__ticket-icon"></i>
        <span class="mf-panel__ticket-label">보유 월운세분석권</span>
      </div>
      <span class="mf-panel__ticket-count">{{ ticketCount }}건</span>
    </div>

    <!-- 낭만 편지 안내 박스 -->
    <div v-if="monthlyCoverCopy" class="mf-panel__intro">
      <h3 class="mf-panel__intro-title">{{ monthlyCoverCopy.headline }}</h3>
      <div class="mf-panel__intro-body">
        <p v-for="(p, i) in monthlyCoverCopy.paragraphs" :key="i" v-html="p"></p>
      </div>
    </div>

    <!-- 구매한 내역 -->
    <div class="mf-panel__history">
      <h4 class="mf-panel__history-title">구매한 내역</h4>
      <template v-if="purchasedTabs.length">
        <div class="mf-panel__history-tabs">
          <button
            v-for="item in purchasedTabs"
            :key="`${item.year}-${item.month}`"
            type="button"
            class="mf-panel__history-tab"
            :class="{ active: isActiveTab(item) }"
            @click="handleSelectTab(item)"
          >
            {{ item.month }}월
          </button>
        </div>
        <div v-if="purchasedCards.length" class="mf-panel__history-card">
          <button
            v-for="card in purchasedCards"
            :key="card.key"
            type="button"
            class="mf-panel__history-card-item"
            @click="handleCardClick(card)"
          >
            <span class="mf-panel__history-card-emoji">{{ card.emoji }}</span>
            <span class="mf-panel__history-card-title">{{ card.title }}</span>
          </button>
        </div>
      </template>
      <div v-else class="mf-panel__history-empty-card">
        <div class="mf-panel__history-empty">구매한 이력이 없습니다.</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.mf-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 28px;
  gap: 20px;
  box-sizing: border-box;
  background: #fff;
  overflow-y: auto;
}

// 보유 이용권 카드
.mf-panel__ticket {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #ebebeb;
  background: #fff;
}
.mf-panel__ticket-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mf-panel__ticket-icon {
  width: 20px;
  height: 20px;
  background: url('@manseImages/ticket-icon.svg') center/contain no-repeat;
}
.mf-panel__ticket-label {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}
.mf-panel__ticket-count {
  color: #eb4c10;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
}
// 낭만 편지 안내
.mf-panel__intro {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 30px;
  border-radius: 12px;
  background: rgba(94, 159, 28, 0.05);
}
.mf-panel__intro-title {
  margin: 0;
  color: #5e9f1c;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
}
.mf-panel__intro-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 20px;
  border-radius: 5px;
  background: #fff;

  p {
    margin: 0;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }

  :deep(.mf-cover-copy__strong) {
    font-weight: 600;
    color: #333;
  }

  :deep(.mf-cover-copy__accent) {
    font-style: normal;
    font-weight: 600;
    color: #5e9f1c;
  }
}

// 구매한 내역
.mf-panel__history {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mf-panel__history-title {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}
.mf-panel__history-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.mf-panel__history-empty-card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100px;
  border: 1px solid #ebebeb;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
}
.mf-panel__history-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 20px;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  box-sizing: border-box;
}
.mf-panel__history-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 20px;
  gap: 8px;
  border: 1px solid #ebebeb;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
}
.mf-panel__history-card-item {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 24px;
  padding: 0 5px;
  border: none;
  border-bottom: 1px dashed #ebebeb;
  background: transparent;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8f8f8;
  }
}
.mf-panel__history-card-emoji {
  font-weight: 700;
}
.mf-panel__history-card-title {
  font-weight: 500;
}
.mf-panel__history-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 15px;
  border: 1px solid transparent;
  border-radius: 100px;
  background: #f8f8f8;
  color: #757575;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &.active {
    background: #fff8f5;
    border-color: #ff8800;
    color: #eb4c10;
    font-weight: 700;
  }
  &:hover:not(.active) {
    background: #f0f0f0;
  }
}
</style>
